import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowLeft, Search, MapPin, Globe, Crosshair, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminOsmSearch() {
  const [tab, setTab] = useState("city");
  const [cities, setCities] = useState([]);
  const [citySlug, setCitySlug] = useState("");
  const [radius, setRadius] = useState(3000);
  const [completeOnly, setCompleteOnly] = useState(false);
  const [coords, setCoords] = useState(null); // {lat, lng}
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("GRATIS");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [meta, setMeta] = useState({ count: 0 });

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data)); }, []);

  const getMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Seu navegador não suporta geolocalização"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); toast.success("Localização capturada"); },
      (err) => toast.error("Falha ao obter localização: " + err.message),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const search = async () => {
    setBusy(true); setResults([]); setSelected(new Set());
    try {
      const payload = { mode: tab, complete_only: completeOnly };
      if (tab === "city") {
        if (!citySlug) { toast.error("Escolha uma cidade"); setBusy(false); return; }
        payload.city_slug = citySlug;
      } else {
        if (!coords) { toast.error("Capture sua localização primeiro"); setBusy(false); return; }
        payload.lat = coords.lat; payload.lng = coords.lng;
        payload.radius_m = parseInt(radius);
      }
      const { data } = await api.post("/admin/osm/search", payload);
      setResults(data.results);
      setMeta({ count: data.count, city: data.city, mode: data.mode });
      toast.success(`${data.count} empresas encontradas`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggle = (idx) => {
    const next = new Set(selected);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    setSelected(next);
  };
  const selectAll = () => {
    const all = new Set();
    results.forEach((r, i) => { if (!r.already_registered) all.add(i); });
    setSelected(all);
  };
  const clearAll = () => setSelected(new Set());

  const importNow = async () => {
    if (selected.size === 0) { toast.error("Selecione ao menos uma empresa"); return; }
    if (tab === "radius" && !citySlug) { toast.error("Para cadastrar, escolha a cidade onde as empresas serão registradas"); return; }
    setBusy(true);
    try {
      const items = Array.from(selected).map((i) => results[i]);
      const { data } = await api.post("/admin/osm/import", {
        city_slug: citySlug, plan, owner_email: ownerEmail || null, items,
      });
      toast.success(`Importadas ${data.created} empresas${data.skipped ? `, ${data.skipped} ignoradas (já existiam)` : ""}.`);
      if (data.owner_temp_password) {
        toast.info(`Senha temporária do comerciante: ${data.owner_temp_password}`, { duration: 30000 });
      }
      search();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/admin" className="text-sm text-stone-600 hover:text-stone-900 inline-flex items-center gap-1 mb-3"><ArrowLeft size={14}/> Voltar</Link>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 flex items-center gap-2"><Globe size={28}/> Localizar Empresas (OpenStreetMap)</h1>
      <p className="text-stone-500 mt-1 text-sm">Busque empresas reais por <strong>Cidade</strong> (exata) ou por <strong>Raio</strong> ao redor da sua localização atual.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-stone-100 rounded-xl">
          <TabsTrigger value="city" data-testid="mode-city"><MapPin size={14} className="mr-1.5"/> Buscar por Cidade</TabsTrigger>
          <TabsTrigger value="radius" data-testid="mode-radius"><Crosshair size={14} className="mr-1.5"/> Buscar por Raio (minha localização)</TabsTrigger>
        </TabsList>

        <TabsContent value="city" className="mt-4">
          <Card className="p-5 rounded-2xl border-stone-200 grid sm:grid-cols-4 gap-3 items-end">
            <div className="sm:col-span-2">
              <Label>Cidade alvo (busca estrita por nome)</Label>
              <Select value={citySlug} onValueChange={setCitySlug}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="osm-city"><SelectValue placeholder="Selecione"/></SelectTrigger>
                <SelectContent>
                  {cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}/{c.state}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium mt-6">
              <Checkbox checked={completeOnly} onCheckedChange={setCompleteOnly} data-testid="osm-complete"/> Apenas com dados completos
            </label>
            <Button onClick={search} disabled={busy || !citySlug} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="osm-search">
              <Search size={14} className="mr-1"/> {busy ? "Buscando..." : "Buscar"}
            </Button>
          </Card>
        </TabsContent>

        <TabsContent value="radius" className="mt-4">
          <Card className="p-5 rounded-2xl border-stone-200 grid sm:grid-cols-5 gap-3 items-end">
            <div className="sm:col-span-2">
              <Label>Minha localização</Label>
              <div className="flex items-center gap-2 mt-1">
                {coords
                  ? <span className="text-sm font-mono px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl flex-1">{coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}</span>
                  : <span className="text-sm text-stone-500 px-3 py-2 bg-stone-50 border border-stone-200 rounded-xl flex-1">Não capturada</span>}
                <Button type="button" onClick={getMyLocation} variant="outline" className="rounded-xl" data-testid="osm-geo">
                  <Crosshair size={14}/>
                </Button>
              </div>
            </div>
            <div>
              <Label>Raio (m)</Label>
              <Input type="number" value={radius} onChange={(e) => setRadius(e.target.value)} className="rounded-xl mt-1" data-testid="osm-radius"/>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium mt-6">
              <Checkbox checked={completeOnly} onCheckedChange={setCompleteOnly} data-testid="osm-complete-radius"/> Dados completos
            </label>
            <Button onClick={search} disabled={busy || !coords} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="osm-search-radius">
              <Search size={14} className="mr-1"/> {busy ? "Buscando..." : "Buscar"}
            </Button>
          </Card>
        </TabsContent>
      </Tabs>

      {results.length > 0 && (
        <Card className="mt-6 p-5 rounded-2xl border-stone-200">
          <div className="flex flex-wrap items-end gap-3 justify-between mb-4">
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label>Cidade onde cadastrar</Label>
                <Select value={citySlug} onValueChange={setCitySlug}>
                  <SelectTrigger className="rounded-xl mt-1 min-w-[160px]" data-testid="osm-target-city"><SelectValue placeholder="Selecione"/></SelectTrigger>
                  <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}/{c.state}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Plano</Label>
                <Select value={plan} onValueChange={setPlan}>
                  <SelectTrigger className="rounded-xl mt-1 min-w-[140px]" data-testid="osm-plan"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GRATIS">Grátis</SelectItem>
                    <SelectItem value="BRONZE">Bronze</SelectItem>
                    <SelectItem value="PRATA">Prata</SelectItem>
                    <SelectItem value="OURO">Ouro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>E-mail comerciante (opcional)</Label>
                <Input value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="contato@empresa.com" className="rounded-xl mt-1 w-[260px]" data-testid="osm-owner-email"/>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={selectAll} className="rounded-lg text-xs">Selecionar todos</Button>
              <Button variant="outline" onClick={clearAll} className="rounded-lg text-xs">Limpar</Button>
              <Button onClick={importNow} disabled={busy || selected.size === 0 || !citySlug} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg" data-testid="osm-import">
                Cadastrar {selected.size} como {plan}
              </Button>
            </div>
          </div>

          <p className="text-xs text-stone-500 mb-3">{meta.count} resultados {meta.city && `em ${meta.city.name}`} {meta.mode === "radius" && `(raio ${radius}m)`} {completeOnly && "· apenas com dados completos"}</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {results.map((r, i) => (
              <div key={r.osm_id} className={`p-4 rounded-2xl border transition-all ${r.already_registered ? "bg-stone-50 border-stone-200 opacity-60" : selected.has(i) ? "bg-blue-50 border-blue-400" : "bg-white border-stone-200 hover:border-stone-400"}`} data-testid={`osm-result-${i}`}>
                <div className="flex items-start gap-2">
                  {!r.already_registered && (
                    <Checkbox checked={selected.has(i)} onCheckedChange={() => toggle(i)} className="mt-1" data-testid={`osm-select-${i}`}/>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-display font-semibold text-stone-900 text-sm">{r.name}</span>
                      {r.is_complete && <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-100 text-emerald-800"><CheckCircle2 size={9}/> Completo</span>}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5">{r.category}</div>
                    {r.address && <div className="text-[11px] text-stone-500 mt-1 flex items-start gap-1"><MapPin size={11} className="mt-0.5 shrink-0"/>{r.address}</div>}
                    {r.phone && <div className="text-[11px] text-stone-600 mt-1">📞 {r.phone}</div>}
                    {r.website && <div className="text-[11px] text-blue-700 mt-1 truncate">{r.website}</div>}
                    {r.already_registered && <span className="inline-block mt-2 px-2 py-0.5 rounded-md bg-stone-200 text-stone-700 text-[10px] font-bold uppercase">Já cadastrada</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
